import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileText, Upload, Download, Trash2, Clock, Wrench, Shield, Droplets,
  ArrowLeft, Ticket, Calendar, DollarSign, Info, Plus, RefreshCw, ChevronRight, History
} from 'lucide-react';
import { AuditLogTimeline } from '../components/AuditLogDialog';

const DOC_TYPES = [
  { value: 'manual', label: 'Manual utilizare' },
  { value: 'schema_electrica', label: 'Schema electrica' },
  { value: 'schema_hidraulica', label: 'Schema hidraulica' },
  { value: 'certificat', label: 'Certificat conformitate' },
  { value: 'garantie', label: 'Certificat garantie' },
  { value: 'fisa_tehnica', label: 'Fisa tehnica' },
  { value: 'procedura', label: 'Procedura mentenanta' },
  { value: 'altele', label: 'Altele' },
];

const EquipmentPassportPage = () => {
  const { equipmentId } = useParams();
  const navigate = useNavigate();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSpecsDialog, setShowSpecsDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDocType, setUploadDocType] = useState('manual');
  const [uploadDesc, setUploadDesc] = useState('');
  const [specsForm, setSpecsForm] = useState({
    power: '', voltage: '', pressure: '', weight: '', dimensions: '', capacity: '', speed: '',
    custom_fields: []
  });

  const fetchPassport = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/equipment-passport/${equipmentId}`);
      setPassport(res.data);
      if (res.data.specs) {
        setSpecsForm(prev => ({ ...prev, ...res.data.specs }));
      }
    } catch (error) {
      toast.error('Eroare la incarcarea pasaportului');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => { fetchPassport(); }, [fetchPassport]);

  const handleSaveSpecs = async () => {
    try {
      await api.post(`/equipment-passport/${equipmentId}/specs`, specsForm);
      toast.success('Specificatii salvate');
      setShowSpecsDialog(false);
      fetchPassport();
    } catch { toast.error('Eroare la salvare'); }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) return;
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('doc_type', uploadDocType);
      formData.append('description', uploadDesc);
      await api.post(`/equipment-passport/${equipmentId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document incarcat');
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadDesc('');
      fetchPassport();
    } catch { toast.error('Eroare la incarcare'); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Stergeti documentul?')) return;
    try {
      await api.delete(`/equipment-passport/${equipmentId}/documents/${docId}`);
      toast.success('Document sters');
      fetchPassport();
    } catch { toast.error('Eroare la stergere'); }
  };

  const handleDownloadDoc = async (docId, filename) => {
    try {
      const res = await api.get(`/equipment-passport/${equipmentId}/documents/${docId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error('Eroare la descarcare'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!passport) {
    return <div className="p-8 text-center text-slate-500">Echipamentul nu a fost gasit.</div>;
  }

  const eq = passport.equipment;
  const specs = passport.specs || {};
  const costs = passport.costs || {};

  const statusColor = (status) => {
    if (!status) return 'bg-slate-100 text-slate-600';
    const s = status.toLowerCase();
    if (s === 'resolved' || s === 'completed' || s === 'valid') return 'bg-green-100 text-green-700';
    if (s === 'open' || s === 'expired' || s === 'critical') return 'bg-red-100 text-red-700';
    if (s === 'in_progress' || s === 'expiring_soon' || s === 'warning') return 'bg-orange-100 text-orange-700';
    return 'bg-blue-100 text-blue-700';
  };

  const typeIcon = (type) => {
    if (type === 'ticket') return <Ticket className="w-4 h-4 text-red-500" />;
    if (type === 'revision') return <Wrench className="w-4 h-4 text-blue-500" />;
    if (type === 'iscir') return <Shield className="w-4 h-4 text-purple-500" />;
    if (type === 'lubrication') return <Droplets className="w-4 h-4 text-amber-500" />;
    return <Clock className="w-4 h-4 text-slate-400" />;
  };

  return (
    <div className="space-y-6" data-testid="equipment-passport-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="passport-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Pasaport Digital</h1>
          <p className="text-slate-500 mt-1">{eq.name} ({eq.code})</p>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{passport.tickets_count}</p>
            <p className="text-xs text-slate-500">Tichete</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{passport.revisions_count}</p>
            <p className="text-xs text-slate-500">Revizii</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{passport.iscir_count}</p>
            <p className="text-xs text-slate-500">ISCIR</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{passport.lubrication_count}</p>
            <p className="text-xs text-slate-500">Lubrifieri</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#0052CC]">{costs.total_parts_cost?.toLocaleString('ro-RO')} RON</p>
            <p className="text-xs text-slate-500">Cost piese</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#0052CC]">{costs.total_labor_hours}h</p>
            <p className="text-xs text-slate-500">Ore manopera</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info" className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="info" data-testid="passport-tab-info">Informatii</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="passport-tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="audit" data-testid="passport-tab-audit">Istoric Modificari</TabsTrigger>
          <TabsTrigger value="documents" data-testid="passport-tab-documents">Documente</TabsTrigger>
          <TabsTrigger value="costs" data-testid="passport-tab-costs">Costuri</TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg">Date Generale</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {[
                  ['Denumire', eq.name],
                  ['Cod', eq.code],
                  ['Fabrica', eq.plant_name],
                  ['Locatie', eq.location_name],
                  ['Categorie', eq.category_name],
                  ['Utilizare', eq.utilizare],
                  ['An Fabricatie', eq.an_fabricatie],
                  ['Putere Absorbita', eq.putere_absorbita],
                  ['Producator', eq.manufacturer],
                  ['Model', eq.model],
                  ['Serie', eq.serial_number],
                  ['Data instalare', eq.installation_date ? new Date(eq.installation_date).toLocaleDateString('ro-RO') : '-'],
                  ['Garantie pana la', eq.warranty_expires ? new Date(eq.warranty_expires).toLocaleDateString('ro-RO') : '-'],
                ].map(([label, value], i) => (
                  <div key={i} className="flex justify-between items-center py-1 border-b border-slate-50">
                    <span className="text-sm text-slate-500">{label}</span>
                    <span className="text-sm font-medium text-slate-900">{value || '-'}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-lg">Specificatii Tehnice</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => setShowSpecsDialog(true)} data-testid="edit-specs-btn">
                    <Plus className="w-4 h-4 mr-1" /> Editeaza
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  ['Putere', specs.power, 'kW'],
                  ['Tensiune', specs.voltage, 'V'],
                  ['Presiune', specs.pressure, 'bar'],
                  ['Greutate', specs.weight, 'kg'],
                  ['Dimensiuni', specs.dimensions, ''],
                  ['Capacitate', specs.capacity, ''],
                  ['Viteza', specs.speed, ''],
                ].map(([label, value, unit], i) => (
                  <div key={i} className="flex justify-between items-center py-1 border-b border-slate-50">
                    <span className="text-sm text-slate-500">{label}</span>
                    <span className="text-sm font-medium text-slate-900">{value ? `${value} ${unit}` : '-'}</span>
                  </div>
                ))}
                {(specs.custom_fields || []).map((cf, i) => (
                  <div key={`cf-${i}`} className="flex justify-between items-center py-1 border-b border-slate-50">
                    <span className="text-sm text-slate-500">{cf.label}</span>
                    <span className="text-sm font-medium text-slate-900">{cf.value} {cf.unit || ''}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader><CardTitle className="text-lg">Istoric Complet ({passport.timeline?.length || 0} evenimente)</CardTitle></CardHeader>
            <CardContent>
              {(!passport.timeline || passport.timeline.length === 0) ? (
                <p className="text-slate-400 text-center py-8">Niciun eveniment inregistrat</p>
              ) : (
                <div className="space-y-1">
                  {passport.timeline.map((event, i) => (
                    <div key={i} className="flex items-center gap-3 py-2.5 px-3 rounded-md hover:bg-slate-50 border-l-2 border-slate-200">
                      {typeIcon(event.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{event.title}</p>
                        <p className="text-xs text-slate-400">
                          {event.date ? new Date(event.date).toLocaleDateString('ro-RO') : '-'}
                        </p>
                      </div>
                      {event.status && (
                        <Badge className={`text-xs ${statusColor(event.status)}`}>
                          {event.status}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-amber-600" />
                Istoric Modificari
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AuditLogTimeline entityType="equipment" entityId={equipmentId} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Documente ({passport.documents?.length || 0})</CardTitle>
                <Button size="sm" onClick={() => setShowUploadDialog(true)} data-testid="upload-doc-btn">
                  <Upload className="w-4 h-4 mr-1" /> Incarca Document
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(!passport.documents || passport.documents.length === 0) ? (
                <p className="text-slate-400 text-center py-8">Niciun document incarcat</p>
              ) : (
                <div className="space-y-2">
                  {passport.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-3 p-3 rounded-md border border-slate-100 hover:bg-slate-50">
                      <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{doc.filename}</p>
                        <p className="text-xs text-slate-400">
                          {DOC_TYPES.find(d => d.value === doc.doc_type)?.label || doc.doc_type}
                          {doc.description && ` - ${doc.description}`}
                          {' | '}{(doc.size_bytes / 1024).toFixed(0)} KB
                          {' | '}{doc.uploaded_by_name}
                        </p>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => handleDownloadDoc(doc.id, doc.filename)} data-testid={`download-doc-${doc.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDeleteDoc(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Costs Tab */}
        <TabsContent value="costs">
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6 text-center">
                <DollarSign className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                <p className="text-3xl font-bold text-slate-900">{costs.total_parts_cost?.toLocaleString('ro-RO')}</p>
                <p className="text-sm text-slate-500 mt-1">RON - Total piese</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <Clock className="w-8 h-8 mx-auto text-orange-500 mb-2" />
                <p className="text-3xl font-bold text-slate-900">{costs.total_labor_hours}</p>
                <p className="text-sm text-slate-500 mt-1">Ore manopera totale</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <Wrench className="w-8 h-8 mx-auto text-green-500 mb-2" />
                <p className="text-3xl font-bold text-slate-900">{costs.parts_used_count}</p>
                <p className="text-sm text-slate-500 mt-1">Piese consumate</p>
              </CardContent>
            </Card>
          </div>

          {costs.cost_by_year && Object.keys(costs.cost_by_year).length > 0 && (
            <Card className="mt-6">
              <CardHeader><CardTitle className="text-lg">Costuri pe An</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(costs.cost_by_year).sort(([a], [b]) => b.localeCompare(a)).map(([year, val]) => (
                    <div key={year} className="flex items-center justify-between py-2 border-b border-slate-50">
                      <span className="text-sm font-medium text-slate-700">{year}</span>
                      <span className="text-sm font-bold text-slate-900">{val.toLocaleString('ro-RO')} RON</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Specs Dialog */}
      <Dialog open={showSpecsDialog} onOpenChange={setShowSpecsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Specificatii Tehnice</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {[
              ['power', 'Putere (kW)'],
              ['voltage', 'Tensiune (V)'],
              ['pressure', 'Presiune (bar)'],
              ['weight', 'Greutate (kg)'],
              ['dimensions', 'Dimensiuni (LxWxH)'],
              ['capacity', 'Capacitate'],
              ['speed', 'Viteza'],
            ].map(([key, label]) => (
              <div key={key}>
                <Label className="text-xs">{label}</Label>
                <Input
                  value={specsForm[key] || ''}
                  onChange={e => setSpecsForm(p => ({ ...p, [key]: e.target.value }))}
                  data-testid={`specs-${key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSpecsDialog(false)}>Anuleaza</Button>
            <Button onClick={handleSaveSpecs} data-testid="save-specs-btn">Salveaza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Incarca Document</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tip document</Label>
              <Select value={uploadDocType} onValueChange={setUploadDocType}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fisier</Label>
              <Input type="file" onChange={e => setUploadFile(e.target.files[0])} data-testid="doc-file-input" />
            </div>
            <div>
              <Label>Descriere (optional)</Label>
              <Input value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} data-testid="doc-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Anuleaza</Button>
            <Button onClick={handleUploadDocument} disabled={!uploadFile} data-testid="upload-doc-submit-btn">Incarca</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentPassportPage;
